import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVolumeMute,
  faVolumeHigh,
  faCaretRight,
  faPause,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons";
import "./Learn.css";
import { getYoutubeVideoId } from "../../utils/youtube";

export default function VideoPlayer({ url, onComplete }) {
  const [player, setPlayer] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [mute, setMute] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);
  const videoId = getYoutubeVideoId(url);

  useEffect(() => {
    const existingScript = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    );
    if (!existingScript) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }

    const createPlayer = () => {
      if (!videoId || !window.YT || !window.YT.Player) return;
      const ytPlayer = new window.YT.Player("yt-player", {
        videoId,
        playerVars: {
          controls: 0,
          autoplay: 1,
          mute: 1,
          rel: 0,
          showinfo: 0,
          modestbranding: 1,
        },
        events: {
          onReady: (event) => {
            setPlayer(event.target);
            setDuration(event.target.getDuration());
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      window.onYouTubeIframeAPIReady = createPlayer;
    }
  }, [videoId]);

  function get_time(time) {
    const totalSeconds = Math.floor(time);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  useEffect(() => {
    if (playing) player?.playVideo();
    else player?.pauseVideo();
    if (mute) player?.mute();
    else player?.unMute();
  }, [playing, mute, player]);

  useEffect(() => {
    const container = document.querySelector("div.full-screen");
    const isFullscreen = !!document.fullscreenElement;

    if (fullScreen && !isFullscreen) container?.requestFullscreen();
    if (!fullScreen && isFullscreen) document.exitFullscreen();
  }, [fullScreen]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (player) {
        setCurrentTime(player.getCurrentTime());
        setDuration(player.getDuration());
      }
    }, 200);

    return () => clearInterval(interval);
  }, [player]);

  useEffect(() => {
    if (!duration || !currentTime) return;
    if (currentTime / duration >= 0.9) {
      onComplete?.();
    }
  }, [currentTime, duration, onComplete]);

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    player.seekTo(time);
  };

  return (
    <div className="full-screen">
      <div id="yt-player"></div>
      {!playing && (
        <img
          src={`https://img.youtube.com/vi/${videoId}/sddefault.jpg`}
          className="image-cover"
          alt="cover"
        />
      )}
      <div className="overlay"></div>
      <div className="controls">
        <input
          type="range"
          min={0}
          max={duration}
          step={1}
          value={currentTime}
          onChange={handleSeek}
          style={{ width: "100%" }}
        />
        <div className="bottom-controls">
          <FontAwesomeIcon
            icon={playing ? faPause : faCaretRight}
            onClick={() => setPlaying((p) => !p)}
            className="play"
          />
          <FontAwesomeIcon
            icon={mute ? faVolumeMute : faVolumeHigh}
            className="play"
            onClick={() => setMute((m) => !m)}
          />
          <span className="time-display">{get_time(currentTime)}</span>
          <span className="sep">/</span>
          <span className="time-display">{get_time(duration)}</span>
          <FontAwesomeIcon
            icon={fullScreen ? faCompress : faExpand}
            className="play"
            onClick={() => setFullScreen((f) => !f)}
          />
        </div>
      </div>
    </div>
  );
}
